import {
  Calendar,
  Check,
  CloudOff,
  LogOut,
  Mail,
  Menu,
  Paperclip,
  RefreshCw,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Tooltip, cn } from '@waymail/ui';
import { useEhAdmin } from '../lib/admin';
import { useUi, type Modulo } from '../store/ui';

// `modulo` presente e o que liga o botao; os demais ainda nao existem.
const MODULOS: {
  id: string;
  label: string;
  icon: typeof Mail;
  modulo: Modulo | null;
}[] = [
  { id: 'mail', label: 'E-mail', icon: Mail, modulo: 'mail' },
  { id: 'calendar', label: 'Calendário', icon: Calendar, modulo: null },
  { id: 'contacts', label: 'Contatos', icon: Users, modulo: 'contacts' },
  { id: 'attachments', label: 'Anexos', icon: Paperclip, modulo: 'attachments' },
  { id: 'tasks', label: 'Tarefas', icon: Check, modulo: 'tasks' },
];

// So aparece quando a API confirma o papel (ADR 0067); a protecao e dela.
const ADMIN = { id: 'admin', label: 'Admin', icon: ShieldCheck, modulo: 'admin' as Modulo };

export type SyncStatus = 'idle' | 'syncing' | 'offline';

/**
 * Rail de modulos do print: faixa azul cheia, hamburger no topo, icones
 * brancos, modulo ativo com fundo translucido.
 */
export function Rail({
  sync,
  pendentes = 0,
  onSair,
}: {
  sync: SyncStatus;
  pendentes?: number;
  onSair?: () => void;
}) {
  const toggleSidebar = useUi((s) => s.toggleSidebar);
  const abrirConfiguracoes = useUi((s) => s.abrirConfiguracoes);
  const modulo = useUi((s) => s.modulo);
  const setModulo = useUi((s) => s.setModulo);
  const modulos = useEhAdmin() ? [...MODULOS, ADMIN] : MODULOS;

  return (
    <nav
      aria-label="Modulos"
      className="hidden w-16 shrink-0 flex-col items-center gap-1 bg-rail py-4 md:flex"
    >
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label="Recolher ou expandir o menu"
        className="mb-3 flex size-10 cursor-pointer items-center justify-center rounded-lg text-white/90 transition-colors hover:bg-white/15"
      >
        <Menu size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>

      <ul className="flex flex-col gap-1">
        {modulos.map(({ id, label, icon: Icon, modulo: destino }) => {
          const disponivel = destino !== null;
          const ativo = destino === modulo;
          return (
            <li key={id}>
              <Tooltip content={disponivel ? label : `${label} (em breve)`} side="right">
                <button
                  type="button"
                  aria-label={label}
                  aria-current={ativo ? 'page' : undefined}
                  disabled={!disponivel}
                  {...(destino !== null ? { onClick: () => setModulo(destino) } : {})}
                  className={cn(
                    'flex size-10 cursor-pointer items-center justify-center rounded-lg transition-colors duration-[var(--duration-micro)]',
                    ativo
                      ? 'bg-white/20 text-white'
                      : 'text-white/60 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:hover:bg-transparent',
                  )}
                >
                  <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
                </button>
              </Tooltip>
            </li>
          );
        })}
      </ul>

      <div className="mt-auto flex flex-col items-center gap-1">
        <SyncIndicator status={sync} pendentes={pendentes} />
        <Tooltip content="Configurações" side="right">
          <button
            type="button"
            onClick={() => abrirConfiguracoes()}
            aria-label="Configurações"
            aria-current={modulo === 'settings' ? 'page' : undefined}
            className={cn(
              'flex size-10 cursor-pointer items-center justify-center rounded-lg transition-colors',
              modulo === 'settings'
                ? 'bg-white/20 text-white'
                : 'text-white/60 hover:bg-white/10 hover:text-white',
            )}
          >
            <Settings size={20} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </Tooltip>
        <Tooltip content="Sair" side="right">
          <button
            type="button"
            {...(onSair !== undefined ? { onClick: onSair } : {})}
            aria-label="Sair"
            className="flex size-10 cursor-pointer items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <LogOut size={20} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
    </nav>
  );
}

/** Secao 11: girando durante sync, `cloud-off` offline, nada quando em dia. */
export function SyncIndicator({
  status,
  pendentes = 0,
}: {
  status: SyncStatus;
  pendentes?: number;
}) {
  if (status === 'idle') return null;

  const offline = status === 'offline';
  const Icon = offline ? CloudOff : RefreshCw;
  const espera =
    pendentes === 0
      ? ''
      : pendentes === 1
        ? '. Uma alteração esperando'
        : `. ${pendentes} alterações esperando`;
  const texto = `${offline ? 'Sem conexão' : 'Sincronizando'}${espera}`;

  return (
    <Tooltip content={texto} side="right">
      <span
        role="status"
        aria-live="polite"
        aria-label={texto}
        className="flex size-10 items-center justify-center text-white/80"
      >
        <Icon
          size={20}
          strokeWidth={1.5}
          aria-hidden="true"
          className={offline ? undefined : 'animate-spin motion-reduce:animate-none'}
        />
      </span>
    </Tooltip>
  );
}
